import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../../../auth/AuthContext";
import * as authApi from "../../../api/auth";
import { teacherGetRunParticipants, teacherGetRuns } from "../../../api/academic";
import { capitalizeFirstLetter, capitalizeWords } from "../../../utils/letters";
import {
	formatDate as formatServerDate,
	formatDateTime as formatServerDateTime,
} from "../../../utils/date";

function formatDate(d) {
	const formatted = formatServerDate(d);
	if (!formatted) return d ? String(d) : "";
	return formatted.replaceAll("-", "/");
}

function formatDateTime(d) {
	if (!d) return "Nunca";
	const formatted = formatServerDateTime(d);
	if (!formatted) return String(d);
	return formatted.replaceAll("-", "/");
}

function roleLabel(role) {
	const labels = {
		teacher: "Docente",
		admin: "Administrador",
		student: "Estudiante",
		company_admin: "Encargado Empresa",
	};
	return labels[role] || "Usuario";
}

function formatSaveError(err) {
	const d = err?.response?.data;
	if (!d) return "No se pudieron guardar los cambios.";
	if (typeof d === "string") return d;
	if (d.detail) return Array.isArray(d.detail) ? d.detail[0] : String(d.detail);
	if (d.details && typeof d.details === "object") {
		const detailMsg = Object.values(d.details).flat().find(Boolean);
		if (detailMsg) return String(detailMsg);
	}
	const msg = Object.values(d).flat().find(Boolean);
	return msg ? String(msg) : "No se pudieron guardar los cambios.";
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const STATUS_LABEL = {
	open: "Abierto",
	closed: "Cerrado",
	draft: "Borrador",
};

const emptyForm = {
	username: "",
	first_name: "",
	last_name: "",
	email: "",
};

export default function TeacherProfile() {
	const { user, refreshUser } = useAuth();
	const [me, setMe] = useState(null);
	const [runs, setRuns] = useState([]);
	const [loadingUser, setLoadingUser] = useState(true);
	const [loadingRuns, setLoadingRuns] = useState(true);
	const [isEditing, setIsEditing] = useState(false);
	const [saving, setSaving] = useState(false);
	const [formData, setFormData] = useState(emptyForm);
	const [error, setError] = useState(null);
	const [success, setSuccess] = useState(null);
	const [studentsInCharge, setStudentsInCharge] = useState(0);
	const [loadingStudents, setLoadingStudents] = useState(false);

	useEffect(() => {
		let cancelled = false;
		authApi
			.me()
			.then((data) => {
				if (!cancelled) setMe(data);
			})
			.catch(() => {
				if (!cancelled) setMe(null);
			})
			.finally(() => {
				if (!cancelled) setLoadingUser(false);
			});
		return () => {
			cancelled = true;
		};
	}, []);

	useEffect(() => {
		let cancelled = false;
		teacherGetRuns()
			.then((data) => {
				if (!cancelled) setRuns(Array.isArray(data) ? data : []);
			})
			.catch(() => {
				if (!cancelled) setRuns([]);
			})
			.finally(() => {
				if (!cancelled) setLoadingRuns(false);
			});
		return () => {
			cancelled = true;
		};
	}, []);

	useEffect(() => {
		let cancelled = false;

		if (loadingRuns) return () => {
			cancelled = true;
		};

		if (!runs.length) {
			setStudentsInCharge(0);
			setLoadingStudents(false);
			return () => {
				cancelled = true;
			};
		}

		setLoadingStudents(true);
		Promise.allSettled(runs.map((run) => teacherGetRunParticipants(run.id)))
			.then((results) => {
				if (cancelled) return;
				const studentIds = new Set();
				results.forEach((result) => {
					if (result.status !== "fulfilled" || !Array.isArray(result.value)) return;
					result.value.forEach((participant) => {
						if (participant?.role === "Estudiante" && participant?.id) {
							studentIds.add(participant.id);
						}
					});
				});
				setStudentsInCharge(studentIds.size);
			})
			.catch(() => {
				if (!cancelled) setStudentsInCharge(0);
			})
			.finally(() => {
				if (!cancelled) setLoadingStudents(false);
			});

		return () => {
			cancelled = true;
		};
	}, [loadingRuns, runs]);

	const displayName =
		me?.full_name ||
		`${me?.first_name || ""} ${me?.last_name || ""}`.trim() ||
		me?.username ||
		user?.fullName ||
		user?.username ||
		"Docente";

	const assignedRuns = runs.length;
	const activeRuns = runs.filter((r) => r.status === "open").length;
	const nextMilestone = (() => {
		const now = new Date();
		let closest = null;
		runs.forEach((run) => {
			["start_date", "end_date"].forEach((key) => {
				if (!run?.[key]) return;
				const date = new Date(run[key]);
				if (Number.isNaN(date.getTime()) || date < now) return;
				if (!closest || date < closest.date) {
					closest = { date, label: key === "start_date" ? "Próximo inicio" : "Próximo cierre" };
				}
			});
		});
		if (!closest) return { value: "Sin hito", label: "Fechas próximas" };
		return { value: formatDate(closest.date), label: closest.label };
	})();

	const openEdit = () => {
		setFormData({
			username: me?.username ?? "",
			first_name: me?.first_name ?? "",
			last_name: me?.last_name ?? "",
			email: me?.email ?? "",
		});
		setError(null);
		setSuccess(null);
		setIsEditing(true);
	};

	const handleCancel = () => {
		setIsEditing(false);
		setError(null);
		setSuccess(null);
	};

	const handleChange = (e) => {
		const { name, value } = e.target;
		setFormData((prev) => ({ ...prev, [name]: value }));
	};

	const handleSave = async (e) => {
		e.preventDefault();
		setError(null);
		setSuccess(null);
		const payload = {
			username: formData.username.trim(),
			first_name: formData.first_name.trim().replace(/\s+/g, " "),
			last_name: formData.last_name.trim().replace(/\s+/g, " "),
			email: formData.email.trim().toLowerCase(),
		};
		if (!payload.username) {
			setError("El nombre de usuario es obligatorio.");
			return;
		}
		if (!payload.first_name) {
			setError("El nombre es obligatorio.");
			return;
		}
		if (!payload.last_name) {
			setError("El apellido es obligatorio.");
			return;
		}
		if (!EMAIL_REGEX.test(payload.email)) {
			setError("El correo electrónico no tiene un formato válido.");
			return;
		}
		setSaving(true);
		try {
			const updated = await authApi.patchMe(payload);
			setMe(updated);
			setIsEditing(false);
			setSuccess("Datos actualizados correctamente.");
			await refreshUser();
		} catch (err) {
			setError(formatSaveError(err));
		} finally {
			setSaving(false);
		}
	};

	return (
		<div className="teacher-page teacher-course-page p-4 backoffice-tables flex-grow-1 overflow-auto font-n">
			<div className="teacher-profile-header">
				<h1 className="teacher-profile-title">Mi perfil</h1>
			</div>

			{success && !isEditing && (
				<div className="alert alert-success teacher-profile-alert" role="status">
					<span>{success}</span>
				</div>
			)}
			{error && (
				<div className="alert alert-danger teacher-profile-alert" role="alert">
					<span>{error}</span>
				</div>
			)}

			<div className="teacher-profile-top-row">
				<section className="teacher-profile-card teacher-profile-card--user overflow-hidden">
					{loadingUser ? (
						<div className="card-body text-center py-5">
							<p className="teacher-profile-loading m-0">Cargando datos...</p>
						</div>
					) : !me ? (
						<div className="card-body text-center py-5">
							<p className="teacher-profile-loading text-danger m-0">No se pudieron cargar tus datos.</p>
						</div>
					) : (
						<>
							{/* --- CARD HEADER --- */}
							<div className="card-header bg-white border-bottom">
								{!isEditing ? (
									<div className="d-flex justify-content-between align-items-center mb-2">
										<h4 className="mb-0 fw-bold text-dark">{capitalizeWords(displayName)}</h4>
										<button type="button" className="adm-upd-btn" onClick={openEdit}>
											<i className="ri-edit-fill me-md-1 d-md-none d-inline"></i>
											<span className="d-none d-md-inline">Editar</span>
										</button>
									</div>
								) : (
									<div className="d-flex justify-content-between align-items-center mb-2">
										<h4 className="mb-0 fw-bold text-dark">Editando Perfil</h4>
										<div className="d-flex gap-2">
											<button type="button" className="adm-del-btn" onClick={handleCancel} disabled={saving}>
												<i className="ri-close-circle-line d-inline d-md-none"></i>
												<span className="d-none d-md-inline">Cancelar</span>
											</button>
											<button type="submit" form="profile-form" className="adm-save-btn" disabled={saving}>
												<i className="ri-checkbox-circle-line me-md-1 d-md-none d-inline"></i>
												<span className="d-none d-md-inline">{saving ? "Guardando..." : "Guardar Cambios"}</span>
											</button>
										</div>
									</div>
								)}
							</div>

							{/* --- CARD BODY --- */}
							<div className="card-body p-3">
								{!isEditing ? (
									<div className="teacher-profile-identity-list mt-3">
										<div className="teacher-profile-identity-item">
											<i className="ri-user-line" aria-hidden />
											<p>Usuario:</p>
											<span>{me.username || "No registrado"}</span>
										</div>
										<div className="teacher-profile-identity-item">
											<i className="ri-mail-line" aria-hidden />
											<p>Email:</p>
											<span>{me.email || "No registrado"}</span>
										</div>
										<div className="teacher-profile-identity-item">
											<i className="ri-shield-user-line" aria-hidden />
											<p>Rol:</p>
											<span>{roleLabel(me.role)}</span>
										</div>
										<div className="teacher-profile-identity-item">
											<i className="ri-building-2-line" aria-hidden />
											<p>Empresa:</p>
											<span>{me.company_name || "Sin empresa"}</span>
										</div>
										<div className="teacher-profile-identity-item">
											<i className="ri-login-box-line" aria-hidden />
											<p>Último acceso:</p>
											<span>{formatDateTime(me.last_login)}</span>
										</div>
										<div className="teacher-profile-identity-item">
											<i className="ri-calendar-check-line" aria-hidden />
											<p>Miembro desde:</p>
											<span>{formatDate(me.date_joined)}</span>
										</div>
									</div>
								) : (
									<form id="profile-form" className="teacher-profile-form" onSubmit={handleSave}>
										<div className="row g-3">
											{/* Campo Usuario Bloqueado */}
											<div className="col-12 mb-2">
												<label className="form-label" htmlFor="tp-username">Usuario:</label>
												<input id="tp-username" className="form-control bg-light text-muted" value={formData.username} readOnly disabled placeholder="Usuario" />
												<div className="d-flex">
													<i className="ri-information-line me-1"></i><span className="small text-muted d-block mt-1">El nombre de usuario no es editable.</span>
												</div>
											</div>

											<div className="col-md-6">
												<label className="form-label" htmlFor="tp-first"><span>Nombres:</span></label>
												<input id="tp-first" name="first_name" className="form-control" value={formData.first_name} onChange={handleChange} autoComplete="given-name" placeholder="Ej: Juan" />
											</div>

											<div className="col-md-6">
												<label className="form-label" htmlFor="tp-last"><span>Apellidos:</span></label>
												<input id="tp-last" name="last_name" className="form-control" value={formData.last_name} onChange={handleChange} autoComplete="family-name" placeholder="Ej: Perez" />
											</div>

											<div className="col-12 mt-3">
												<label className="form-label" htmlFor="tp-email"><span>Email:</span></label>
												<input id="tp-email" name="email" type="email" className="form-control" value={formData.email} onChange={handleChange} autoComplete="email" placeholder="Ej: nombre@correo.com" />
											</div>
										</div>
									</form>
								)}
							</div>
						</>
					)}
				</section>

				<section className="teacher-profile-card teacher-profile-stats teacher-profile-card--summary">
					<div className="card-header bg-white border-bottom">
						<h4 className="mb-0 fw-bold text-dark mb-2">Resumen</h4>
					</div>

					{loadingRuns ? (
						<p className="teacher-profile-loading">Cargando cursos...</p>
					) : (
						<div className="teacher-profile-stats-grid p-2 py-4">
							<div className="teacher-profile-stat-card teacher-profile-stat-card--asig">
								<p className="teacher-profile-stat-value">{assignedRuns}</p>
								<span>{assignedRuns === 1 ? "Curso asignado" : "Cursos asignados"}</span>
							</div>
							<div className="teacher-profile-stat-card teacher-profile-stat-card--actv">
								<p className="teacher-profile-stat-value">{activeRuns}</p>
								<span>Cursos activos</span>
							</div>
							<div className="teacher-profile-stat-card teacher-profile-stat-card--std">
								<p className="teacher-profile-stat-value">{loadingStudents ? "..." : studentsInCharge}</p>
								<span>Estudiantes a cargo</span>
							</div>
							<div className="teacher-profile-stat-card teacher-profile-stat-card--next">
								<p className="teacher-profile-stat-value teacher-profile-stat-value--date">{nextMilestone.value}</p>
								<span>{nextMilestone.label}</span>
							</div>
						</div>
					)}
				</section>
			</div>

			<section className="teacher-profile-card teacher-profile-courses">
				<div className="card-header bg-white border-bottom">
					<h4 className="mb-0 fw-bold text-dark mb-2">Mis Cursos</h4>
				</div>

				{loadingRuns ? (
					<p className="teacher-profile-loading">Cargando cursos...</p>
				) : runs.length === 0 ? (
					<p className="teacher-profile-empty">No tienes cursos asignados.</p>
				) : (
					<div className=" p-2 py-4">
						<ul className="teacher-profile-course-list">
							{runs.map((run) => (
								<li key={run.id} className="teacher-profile-course-item">
									<Link to={`/portal-doc/cursos/${run.id}`} className="teacher-profile-course-link">
										<div className="teacher-profile-course-image">
											{run.image ? (
												<img src={run.image} alt="" />
											) : (
												<div className="teacher-profile-course-placeholder">
													<i className="ri-book-2-line" aria-hidden />
												</div>
											)}
										</div>
										<div className="teacher-profile-course-body">
											<div className="d-flex gap-1 mb-2">
												<h3 className="teacher-profile-course-name mb-0">
													{capitalizeFirstLetter(run.name) || run.code || `Curso ${run.id}`} -
												</h3>
												{run.code && run.name && (
													<p className="teacher-profile-course-code mb-0">{run.code}</p>
												)}</div>
											<div className="teacher-profile-course-dates">
												{run.start_date && (
													<div className="d-flex gap-2">
														<i className="ri-calendar-line" aria-hidden />
														<span> Inicio:{" "} {formatDate(run.start_date)}</span>
													</div>
												)}
												{run.end_date && (
													<div className="d-flex gap-2">
														<i className="ri-calendar-check-line" aria-hidden />
														<span>Fin:{" "}{formatDate(run.end_date)}</span>
													</div>
												)}
											</div>
											{run.status && (
												<span
													className={`teacher-profile-course-status teacher-profile-course-status--${run.status}`}
												>
													{STATUS_LABEL[run.status] || run.status}
												</span>
											)}
										</div>
										<i className="ri-arrow-right-s-line teacher-profile-course-arrow" aria-hidden />
									</Link>
								</li>
							))}
						</ul>
					</div>
				)}
			</section>
		</div>
	);
}
